import { Check } from 'lucide-react';
import { Badge } from './Badge';
import { Tooltip } from './Tooltip';
import { TIER_DETAILS } from '@/lib/glossary';
import { tierLabel, tierTone } from '@/lib/tier';
import type { Tier } from '@/data/types';

interface TierBadgeProps {
  tier: Tier;
  className?: string;
}

/**
 * Badge for a plan tier that, on hover, shows the price, runtime model,
 * limits, and key features for that tier. Removes the need for users to
 * remember what Starter / Team / Business / Enterprise mean.
 * Custom plans (MC-611) have no glossary entry — they render as a plain
 * badge; their details live on the Plans page.
 */
export function TierBadge({ tier, className }: TierBadgeProps) {
  const detail = TIER_DETAILS[tier] as (typeof TIER_DETAILS)[string] | undefined;

  if (!detail) {
    return (
      <Badge tone={tierTone(tier)} className={className}>
        {tierLabel(tier)}
      </Badge>
    );
  }

  return (
    <Tooltip
      maxWidth={320}
      content={
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-semibold">{tierLabel(tier)}</span>
            <span className="text-[11px] opacity-90 tabular-nums">{detail.price}</span>
          </div>
          <p className="text-[11px] opacity-90 leading-snug">{detail.description}</p>
          <div className="border-t border-bg/20 pt-1.5 space-y-1">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Runtime</div>
            <div className="text-[11px]">{detail.runtime}</div>
          </div>
          <div className="border-t border-bg/20 pt-1.5 space-y-0.5">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Limits</div>
            {detail.limits.map(l => (
              <div key={l} className="text-[11px] opacity-90">· {l}</div>
            ))}
          </div>
          <div className="border-t border-bg/20 pt-1.5 space-y-0.5">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Includes</div>
            {detail.features.map(f => (
              <div key={f} className="text-[11px] opacity-90 flex items-start gap-1">
                <Check size={9} className="mt-0.5 shrink-0 opacity-70" /> {f}
              </div>
            ))}
          </div>
        </div>
      }
    >
      <Badge tone={tierTone(tier)} className={className}>
        {tierLabel(tier)}
      </Badge>
    </Tooltip>
  );
}
